import { useState, useRef, useEffect } from "react";
import { CoreModal } from "../core/modal/Modal";
import styles from "./installLockdown.module.css";
import { CoreButton, CoreIconButton } from "../core/button/Button";
import { CoreTextBox } from "../core/textbox/TextBox";
import { Link, Typography } from "@mui/material";
import { coreToast, CoreToastContainer } from "../core/toast";
import i18nPackage from "../i18n/i18n";
import { DownloadLockdownModuleButton } from "../downloadButton";
import ThemeWrapper from "../theme";
import Copy from "./images/copy.svg?react";
import Copied from "./images/CheckCircle.svg?react";
import Verity from "./images/verity-icon-with-border.svg?react";
import Download from "./images/download.svg?react";
import Launch from "./images/launch.svg?react";
import Info from "./images/info-with-border.svg?react";
import CaretUp from "./images/caret-up.svg?react";
import CaretDown from "./images/caret-down.svg?react";

const LOCKDOWN_COMPATIBILITY_CHECK_REGEX = /^verity:\/\/check-compatibility#[0-9]+$/;

/**
 * Best-effort Mac architecture guess for browsers without `userAgentData` (Firefox, Safari), which report
 * "MacIntel" on Apple Silicon too. Every uncertain path returns "mac": the Intel build runs on Apple Silicon
 * under Rosetta, but the arm64 build cannot run on Intel at all.
 */
function guessMacArchFromWebGL(): "mac" | "mac-arm" {
	const gl = document.createElement("canvas").getContext("webgl");
	if (!gl) {
		return "mac";
	}
	try {
		const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
		const renderer: string = (debugInfo && (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)) || "";
		// Firefox sanitizes every Apple GPU down to "Apple M1, or similar"; Intel Macs report Intel/AMD instead.
		if (/Apple M\d/.test(renderer)) {
			return "mac-arm";
		}
		// Safari hardcodes "Apple GPU" on Intel Macs as well, so fall back to a GPU family capability:
		// ANGLE only exposes ASTC on Apple family GPUs.
		if (renderer === "Apple GPU" && gl.getSupportedExtensions()?.includes("WEBGL_compressed_texture_astc")) {
			return "mac-arm";
		}
	} catch (e) {
		console.error(e);
	} finally {
		gl.getExtension("WEBGL_lose_context")?.loseContext();
	}
	return "mac"; // intel as fallback
}

export async function determineLockdownDownloadLinkType(): Promise<"mac" | "mac-arm" | "win" | undefined> {
	const userAgentData =
		"userAgentData" in navigator
			? (navigator.userAgentData as
					| {
							getHighEntropyValues?: (info: string[]) => Promise<{ architecture?: string; platform?: string }>;
					  }
					| undefined)
			: undefined;
	if (userAgentData?.getHighEntropyValues) {
		try {
			// chromium only
			const platformInfo = await userAgentData.getHighEntropyValues(["architecture"]);
			if (platformInfo.platform?.toLowerCase().includes("win")) {
				return "win";
			} else if (platformInfo.platform?.toLowerCase().includes("mac")) {
				if (platformInfo.architecture?.toLowerCase().includes("arm")) {
					return "mac-arm";
				} else if (platformInfo.architecture) {
					return "mac";
				}
			}
		} catch (e) {
			console.error(e);
		}
	}
	// eslint-disable-next-line deprecation/deprecation
	if (navigator.platform.includes("Mac")) {
		return guessMacArchFromWebGL();
		// eslint-disable-next-line deprecation/deprecation
	} else if (navigator.platform.includes("Win")) {
		return "win";
	}
	return undefined;
}

export function InstallLockdownModalContent({
	quizUrl,
	launchLockdown,
	getDownloadLink,
	getAccessControlAuthCode,
}: {
	quizUrl: string;
	launchLockdown: () => void;
	getDownloadLink: () => Promise<{ url: string; size?: number } | undefined>;
	getAccessControlAuthCode: () => Promise<{ authCode?: string; accountId?: string } | undefined>;
}) {
	const isCompatibilityCheck = LOCKDOWN_COMPATIBILITY_CHECK_REGEX.test(quizUrl);
	const textInputRef = useRef<HTMLInputElement>(null);
	const [isCopyButtonPressed, setIsCopyButtonPressed] = useState(false);
	const isCopyButtonPressedRef = useRef<boolean>(false);
	const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
	const [isLinkSectionExpanded, setIsLinkSectionExpanded] = useState(false);

	const [launchDisabled, setLaunchDisabled] = useState(false);
	const disableLaunchButton = () => {
		setLaunchDisabled(true);
		setTimeout(() => setLaunchDisabled(false), 5000);
	};
	useEffect(() => {
		disableLaunchButton();
	}, []);

	useEffect(() => {
		void (async () => {
			try {
				const result = await getDownloadLink();
				setDownloadUrl(result?.url);
			} catch (e) {
				console.error(e);
				setDownloadUrl(undefined);
			}
		})();
	}, [getDownloadLink]);

	return (
		<div className={styles.dialogContent}>
			<div className={styles.content}>
				<div className={styles.headerContent}>
					<Verity />
					<Typography
						sx={{
							fontFamily: "Inter, sans-serif",
							fontWeight: 600,
							fontSize: "24px",
							lineHeight: 1.5,
							textAlign: "center",
							color: "#333333",
						}}
					>
						{i18nPackage.t("installLockdown.launchingLockdown")}
					</Typography>
				</div>

				<div className={styles.contentContainer}>
					<div className={styles.stepsContainer}>
						{/* Step 1: Download */}
						<div className={styles.actionRow}>
							<div className={styles.textColumn}>
								<Typography
									sx={{
										fontFamily: "Inter, sans-serif",
										fontWeight: 600,
										fontStyle: "normal",
										fontSize: "16px",
										lineHeight: "24px",
										letterSpacing: 0,
										textAlign: "left",
										color: "#1C1C1E",
										marginBottom: "4px",
									}}
								>
									{i18nPackage.t("installLockdown.stepOne.title")}
								</Typography>
								<Typography
									sx={{
										fontFamily: "Inter, sans-serif",
										fontWeight: 400,
										fontStyle: "normal",
										fontSize: "14px",
										lineHeight: "20px",
										letterSpacing: 0,
										textAlign: "left",
										color: "#5B6473",
									}}
								>
									{i18nPackage.t("installLockdown.stepOne.description")}
								</Typography>
							</div>
							<div className={styles.buttonColumn}>
								<DownloadLockdownModuleButton
									size="medium"
									className={styles.actionButton}
									getAuthCode={getAccessControlAuthCode}
									onError={() => {
										if (!downloadUrl) {
											coreToast.error(i18nPackage.t("installLockdown.windowsOrMacOnlyMsg"));
										}
									}}
									downloadUrl={downloadUrl}
								>
									<Download style={{ marginRight: 8 }} />
									{i18nPackage.t("installLockdown.stepOne.downloadApp")}
								</DownloadLockdownModuleButton>
							</div>
						</div>

						{/* OR Divider */}
						<div className={styles.dividerContainer}>
							<div className={styles.dividerLine} />
							<span className={styles.dividerText}>{i18nPackage.t("installLockdown.or")}</span>
							<div className={styles.dividerLine} />
						</div>

						{/* Step 2: Launch */}
						<div className={styles.actionRow}>
							<div className={styles.textColumn}>
								<Typography
									sx={{
										fontFamily: "Inter, sans-serif",
										fontWeight: 600,
										fontStyle: "normal",
										fontSize: "16px",
										lineHeight: "24px",
										letterSpacing: 0,
										textAlign: "left",
										color: "#1C1C1E",
										marginBottom: "4px",
									}}
								>
									{i18nPackage.t("installLockdown.stepTwo.title")}
								</Typography>
								<Typography
									sx={{
										fontFamily: "Inter, sans-serif",
										fontWeight: 400,
										fontStyle: "normal",
										fontSize: "14px",
										lineHeight: "20px",
										letterSpacing: 0,
										textAlign: "left",
										color: "#5B6473",
									}}
								>
									{isCompatibilityCheck
										? i18nPackage.t("installLockdown.stepTwo.compatibilityCheckDescription")
										: i18nPackage.t("installLockdown.stepTwo.description")}
								</Typography>
							</div>
							<div className={styles.buttonColumn}>
								<CoreButton
									size="medium"
									className={styles.actionButton}
									disabled={launchDisabled}
									onClick={() => {
										disableLaunchButton();
										launchLockdown();
									}}
								>
									<Launch style={{ marginRight: 8 }} />
									{launchDisabled
										? i18nPackage.t("installLockdown.launching")
										: i18nPackage.t("installLockdown.stepTwo.launchApp")}
								</CoreButton>
							</div>
						</div>
					</div>

					{/* Info Note Row */}
					{!isCompatibilityCheck && (
						<div className={styles.infoRow}>
							<Info />
							<Typography
								sx={{
									flex: 1,
									fontFamily: "Inter, sans-serif",
									fontWeight: 400,
									fontStyle: "normal",
									fontSize: "12px",
									lineHeight: "16px",
									letterSpacing: "0px",
									color: "#4A5565",
								}}
							>
								{i18nPackage.t("installLockdown.infoText")}
							</Typography>
						</div>
					)}

					{/* Copy Link Section */}
					<div className={styles.expandableContainer}>
						<div className={styles.expandableHeader}>
							<Typography
								sx={{
									fontFamily: "Inter, sans-serif",
									fontWeight: 600,
									fontSize: "16px",
									lineHeight: "24px",
									color: "#1C1C1E",
								}}
							>
								{isCompatibilityCheck
									? i18nPackage.t("installLockdown.compatibilityCheckLink")
									: i18nPackage.t("installLockdown.lmsAssessmentLink")}
							</Typography>

							<CoreIconButton
								variant="plain"
								onClick={() => setIsLinkSectionExpanded(!isLinkSectionExpanded)}
								aria-expanded={isLinkSectionExpanded}
								aria-label={
									isLinkSectionExpanded
										? i18nPackage.t("installLockdown.collapse")
										: i18nPackage.t("installLockdown.expand")
								}
							>
								{!isLinkSectionExpanded ? <CaretDown /> : <CaretUp />}
							</CoreIconButton>
						</div>

						{/* Expanded Content (Conditionally Rendered) */}
						<div className={styles.dividerLine} style={{ marginTop: "7px" }} />
						{isLinkSectionExpanded && (
							<div className={styles.copySection}>
								<Typography
									sx={{
										fontFamily: "Inter, sans-serif",
										fontWeight: 400,
										fontStyle: "normal",
										fontSize: "14px",
										lineHeight: "20px",
										letterSpacing: "0px",
										color: "#1E2939",
									}}
								>
									{isCompatibilityCheck
										? i18nPackage.t("installLockdown.copyLinkTextCompatibility")
										: i18nPackage.t("installLockdown.copyLinkText")}
								</Typography>

								<div className={styles.linkRow}>
									<CoreTextBox
										value={quizUrl}
										inputRef={textInputRef}
										style={{
											width: "100%",
											height: "42px",
											borderRadius: "10px",
											border: "1px solid #e5e7eb",
											padding: "0px 12px",
											background: "#ffffff",
											display: "flex",
											alignItems: "center",
											fontFamily: '"Inter", sans-serif',
											fontWeight: 400,
											fontStyle: "normal",
											fontSize: "12px",
											lineHeight: "16px",
											letterSpacing: "0px",
											color: "#6a7282",
										}}
										outlineWidth="0px"
										readOnly
									/>

									<CoreIconButton
										variant="plain"
										className={styles.copyButton}
										onClick={() => {
											if (isCopyButtonPressedRef?.current === false) {
												const fallbackCopy = () => {
													textInputRef.current?.focus();
													textInputRef.current?.setSelectionRange(0, quizUrl.length);
													// eslint-disable-next-line deprecation/deprecation
													document.execCommand("copy");
												};
												if (navigator.clipboard) {
													void navigator.clipboard.writeText(quizUrl).catch(fallbackCopy);
												} else {
													fallbackCopy();
												}
												isCopyButtonPressedRef.current = true;
												setIsCopyButtonPressed(true);
												setTimeout(() => {
													setIsCopyButtonPressed(false);
													isCopyButtonPressedRef.current = false;
												}, 3000);
											}
										}}
									>
										{!isCopyButtonPressed ? (
											<>
												<Copy />
												<Typography
													sx={{
														fontFamily: "Inter, sans-serif",
														fontWeight: 500,
														fontStyle: "normal",
														fontSize: "14px",
														lineHeight: "20px",
														letterSpacing: 0,
														textAlign: "center",
														color: "#364153",
													}}
												>
													{i18nPackage.t("installLockdown.copy")}
												</Typography>
											</>
										) : (
											<>
												<Copied />
												<Typography
													sx={{
														fontFamily: "Inter, sans-serif",
														fontWeight: 500,
														fontStyle: "normal",
														fontSize: "14px",
														lineHeight: "20px",
														letterSpacing: 0,
														textAlign: "center",
														color: "#364153",
													}}
												>
													{i18nPackage.t("installLockdown.copied")}
												</Typography>
											</>
										)}
									</CoreIconButton>
								</div>
							</div>
						)}
					</div>
				</div>

				<Link
					className={styles.supportLink}
					href="https://support.yuja.com/hc/en-us"
					target="_blank"
					rel="noopener noreferrer"
				>
					{i18nPackage.t("installLockdown.contactSupport")}
				</Link>
			</div>
		</div>
	);
}

export default function InstallLockdownPage({
	quizUrl,
	launchLockdown,
	getDownloadLink,
	getAccessControlAuthCode,
	onClose,
}: {
	quizUrl: string;
	launchLockdown: () => void;
	getDownloadLink: () => Promise<{ url: string; size?: number } | undefined>;
	getAccessControlAuthCode: () => Promise<{ authCode?: string; accountId?: string } | undefined>;
	onClose?: () => void;
}) {
	const [modalShowing, setModalShowing] = useState(true);

	return (
		<ThemeWrapper>
			<CoreToastContainer />
			<CoreModal
				onClose={() => {
					setModalShowing(false);
					onClose?.();
				}}
				open={modalShowing}
			>
				<InstallLockdownModalContent
					quizUrl={quizUrl}
					launchLockdown={launchLockdown}
					getDownloadLink={getDownloadLink}
					getAccessControlAuthCode={getAccessControlAuthCode}
				/>
			</CoreModal>
		</ThemeWrapper>
	);
}
